import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "w-full" }
const _hoisted_2 = { class: "formgrid grid" }
const _hoisted_3 = { class: "md:col-6 col-12 field" }
const _hoisted_4 = { class: "formgrid grid" }
const _hoisted_5 = {
  key: 0,
  class: "w-2 mt-2 flex align-items-center justify-content-center"
}
const _hoisted_6 = { style: {"font-family":"Rubik","font-size":"14px","width":"auto"} }
const _hoisted_7 = { class: "ml-2" }

import useVuelidate from '@vuelidate/core';
import { useToast } from 'primevue/usetoast';
import Calendar from 'primevue/calendar';
import { StudentInfoFormInterface } from '@/types';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { PersonStores } from '@/store';
import { can, useDateTime } from '@/composables';
import { PERMISSIONS, GRADES_ORDER, GradeKey } from '@/enum';


export default /*@__PURE__*/_defineComponent({
  __name: 'StudentInfo',
  setup(__props) {

const toast = useToast();
const profileStore = PersonStores.useUserProfileStore();

const student = reactive<StudentInfoFormInterface>({
  grade: undefined,
  gender: undefined,
  age: undefined,
  birthday: undefined,
  comment: undefined,
  building_campus: undefined,
});

const genders = [
  { id: 'M', title: 'Male' },
  { id: 'F', title: 'Female' },
];

const grades = GRADES_ORDER as readonly GradeKey[];

//validator rules
const rules = computed(() => {
  return {
    birthday: { lazy: true },
    grade: {},
    gender: {},
    age: {},
    comment: {},
  };
});

const canUpdateStudentInfoDisabled = () => !can([PERMISSIONS.PEOPLE_UPDATE_STUDENT_INFORMATION]);

//set up the validator
const v$ = useVuelidate(rules, student);

const birthdayDate = ref();

const getDate = (date: string) => {
  const [year, month, day] = date.split('-').map((digit) => Number(digit));

  return new Date(year, month - 1, day);
};

const birthdayFormatted = computed(() => {
  if (!birthdayDate.value) return '';
  return birthdayDate.value.toJSON()?.split('T')[0];
});

onMounted(() => {
  setStudentInfo();
  birthdayDate.value = student.birthday ? getDate(student.birthday) : '';
});

/**
 * set the student info. if the student
 */
const setStudentInfo = () => {
  if (profileStore.studentInfo) {
    let { grade, birthday, gender, comment } = profileStore.studentInfo;
    student.grade = grade;
    birthdayDate.value = birthday ? getDate(birthday) : '';
    student.birthday = birthday;
    student.gender = gender;
    student.comment = comment;
  }
};

watch(() => profileStore.studentInfo, setStudentInfo);

/**
 * on cancel of the student Info form
 */
const onStudentInfoCancel = async () => (setStudentInfo(), resetDirty());

const onAttemptToSave = ref(false);

/**
 * update the student info
 */
const onStudentInfoSave = async () => {
  onAttemptToSave.value = true;
  v$.value.$touch();
  if (!v$.value.$invalid) {
    const result = await profileStore.updateStudentInfo(student);

    if (result === 200) {
      doToast('Student Information');
      resetDirty();
    }
  }
  return;
};

/**
 * show the toast
 *
 * @param formName
 */
const doToast = (formName: string) => {
  toast.add({
    severity: 'success',
    summary: 'Saved',
    detail: `${formName} has been saved`,
    life: 3000,
  });
};

const resetDirty = () => {
  if (v$) v$.value.$reset();
};

const fieldStyles = (key: string, styles?: string): string => {
  const validation = v$.value[key].$invalid && v$.value[key].$dirty ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

const maxDate = new Date();
maxDate.setDate(new Date().getDate() - 1);

watch(birthdayFormatted, () => {
  if (birthdayFormatted.value !== profileStore.studentInfo?.birthday) {
    student.birthday = birthdayFormatted.value;
    if (birthdayFormatted.value != '') v$.value.birthday.$touch();
  }
});

const verifyEmpty = (value: string | undefined) => {
  if (value?.length > 1) {
    return;
  }

  return v$.value['birthday'].$touch();
};

return (_ctx: any,_cache: any) => {
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_FormCard = _resolveComponent("FormCard")!

  return (_openBlock(), _createBlock(_component_FormCard, {
    title: "Student Information",
    dirty: _unref(v$).$anyDirty,
    cancel: onStudentInfoCancel,
    save: onStudentInfoSave,
    "save-disabled": _unref(v$).$anyDirty && _unref(v$).$invalid
  }, {
    form: _withCtx(() => [
      _createElementVNode("form", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[5] || (_cache[5] = _createElementVNode("label", { for: "grade" }, "Grade", -1)),
            _createVNode(_component_Dropdown, {
              id: "grade",
              modelValue: _unref(v$).grade.$model,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).grade.$model) = $event)),
              options: _unref(grades),
              placeholder: "Select a Grade",
              class: _normalizeClass(fieldStyles('grade', 'w-full')),
              disabled: canUpdateStudentInfoDisabled()
            }, null, 8, ["modelValue", "options", "class", "disabled"])
          ]),
          _createElementVNode("div", {
            class: _normalizeClass(["field md:col-6 col-12", { error: _unref(v$).gender.$errors.length }])
          }, [
            _cache[6] || (_cache[6] = _createElementVNode("label", { for: "gender" }, "Gender", -1)),
            _createVNode(_component_Dropdown, {
              id: "gender",
              modelValue: _unref(v$).gender.$model,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).gender.$model) = $event)),
              options: genders,
              "option-value": "id",
              "option-label": "title",
              placeholder: "Select a Gender",
              class: _normalizeClass(fieldStyles('gender', 'w-full')),
              disabled: canUpdateStudentInfoDisabled()
            }, null, 8, ["modelValue", "class", "disabled"])
          ], 2)
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", {
            class: _normalizeClass(["field col-10", { error: _unref(v$).birthday.$errors.length }])
          }, [
            _cache[7] || (_cache[7] = _createElementVNode("label", { for: "birthday" }, "Birthdate", -1)),
            _createVNode(_unref(Calendar), {
              id: "birthday",
              modelValue: birthdayDate.value,
              "onUpdate:modelValue": [
                _cache[2] || (_cache[2] = ($event: any) => ((birthdayDate).value = $event)),
                _cache[3] || (_cache[3] = ($event: any) => (verifyEmpty(birthdayDate.value)))
              ],
              placeholder: "mm/dd/yyyy",
              "date-format": "mm/dd/yy",
              birthday: birthdayFormatted.value,
              "max-date": _unref(maxDate),
              "manual-input": true,
              class: _normalizeClass({ 'p-invalid': _unref(v$).birthday.$invalid && _unref(v$).birthday.$dirty }),
              disabled: canUpdateStudentInfoDisabled()
            }, null, 8, ["modelValue", "birthday", "max-date", "class", "disabled"])
          ], 2),
          (birthdayFormatted.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("label", _hoisted_6, [
                  _cache[8] || (_cache[8] = _createTextVNode(" Age ", -1)),
                  _createElementVNode("b", _hoisted_7, _toDisplayString(_unref(useDateTime)().getAge(birthdayFormatted.value)), 1)
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", {
          class: _normalizeClass(["field col-12 p-0", { error: _unref(v$).comment.$errors.length }])
        }, [
          _cache[9] || (_cache[9] = _createElementVNode("label", { for: "notes" }, "Comments", -1)),
          _createVNode(_component_InputText, {
            id: "notes",
            modelValue: student.comment,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((student.comment) = $event)),
            class: _normalizeClass(["inputfield w-full", { 'p-invalid': _unref(v$).comment.$invalid && _unref(v$).comment.$dirty }]),
            disabled: canUpdateStudentInfoDisabled(),
            onInput: _unref(v$).comment.$touch
          }, null, 8, ["modelValue", "class", "disabled", "onInput"])
        ], 2)
      ])
    ]),
    _: 1
  }, 8, ["dirty", "save-disabled"]))
}
}

})